/**
 * Regras de apresentação da Central de Projetos (sem React, testáveis).
 *
 * Cor só comunica estado, e uma vez por linha: atrasado (danger), com
 * divergência ou travado (warning), resto neutro.
 */
import { dataLocalDe, formatarDataLocal, plural } from "../../utils/dinheiro";

export const MINUTOS_MONITOR_OFFLINE = 10;

export const ROTULO_ESPERA = Object.freeze({
  cliente: "A equipe espera o cliente",
  equipe: "Cliente espera a equipe",
  ninguem: "Ninguém esperando",
  indefinido: "",
});

/** Tipo da conversa. A chave `interno` é a equipe da empresa; `desconhecido` ainda não foi classificada. */
export const ROTULO_CLASSIFICACAO = Object.freeze({
  cliente: "Cliente",
  obra: "Obra",
  interno: "Equipe",
  diretoria: "Diretoria",
  desconhecido: "A classificar",
});

/** Tipos que uma pessoa pode escolher (a classificar é só o ponto de partida). */
export const TIPOS_DE_CONVERSA = Object.freeze(["cliente", "obra", "interno", "diretoria"]);

/** Quem espera quem, segundo a prévia da IA (chaves do Jev no backend). */
export const ESPERA_DA_PREVIA = Object.freeze({
  equipe: { rotulo: "Aguardando a equipe", tom: "warning" },
  cliente: { rotulo: "Aguardando o cliente", tom: "info" },
  ninguem: { rotulo: "Em dia", tom: "success" },
});

/** Quem deve cada item de "O que falta" (mesma cor de quem espera). */
const QUEM_FALTA = Object.freeze({
  equipe: { rotulo: "Equipe", tom: "warning" },
  cliente: { rotulo: "Cliente", tom: "info" },
  outro: { rotulo: "Outro", tom: "neutral" },
});

/** Ordem de "Quem precisa agir": primeiro o que a equipe deve. */
const ORDEM_DE_QUEM = Object.freeze(["equipe", "cliente", "outro"]);

/**
 * "O que falta" da prévia agrupado por quem deve agir, na ordem Equipe,
 * Cliente, Outro. Quem vier sem papel conhecido entra em Outro.
 * → [{ quem, rotulo, tom, itens: ["..."] }]
 */
export function quemPrecisaAgir(falta) {
  const grupos = new Map();
  (falta || []).forEach((item) => {
    const texto = String(item?.o_que || "").trim();
    if (!texto) return;
    const quem = QUEM_FALTA[item.quem] ? item.quem : "outro";
    if (!grupos.has(quem)) grupos.set(quem, { quem, ...QUEM_FALTA[quem], itens: [] });
    grupos.get(quem).itens.push(texto);
  });
  return ORDEM_DE_QUEM.filter((quem) => grupos.has(quem)).map((quem) => grupos.get(quem));
}

/** Urgência do Jev em 0..1 (4 níveis: rotina, semana, hoje/amanhã, crítica). */
const URGENCIA_CRITICA = 0.9;
const URGENCIA_HOJE = 0.6;
const URGENCIA_SEMANA = 0.3;

export function urgenciaDaPrevia(valor) {
  if (typeof valor !== "number" || Number.isNaN(valor)) return null;
  if (valor >= URGENCIA_CRITICA) return { rotulo: "Urgência crítica", tom: "danger" };
  if (valor >= URGENCIA_HOJE) return { rotulo: "Agir hoje ou amanhã", tom: "warning" };
  if (valor >= URGENCIA_SEMANA) return { rotulo: "Agir nesta semana", tom: "neutral" };
  return { rotulo: "Sem urgência", tom: "neutral" };
}

/**
 * Linha de estado da prévia: quem espera e a urgência numa frase, com UMA cor
 * (a de quem espera; vermelho quando a urgência é crítica, como na lista).
 * → { tom, texto, curto } ou null (`curto`: só quem espera, para a faixa recolhida)
 */
export function estadoDaPrevia(previa) {
  if (!previa) return null;
  const espera = ESPERA_DA_PREVIA[previa.quem_espera] || null;
  const urgencia = urgenciaDaPrevia(previa.urgencia);
  const critica = (previa.urgencia ?? 0) >= URGENCIA_CRITICA;
  const urgente = urgencia && urgencia.rotulo !== "Sem urgência" ? urgencia.rotulo.toLowerCase() : "";
  const partes = [espera?.rotulo, urgente].filter(Boolean);
  if (!partes.length) return null;
  return { tom: critica ? "danger" : espera?.tom || "neutral", texto: partes.join(" · "), curto: partes[0] };
}

/**
 * Alerta "Projetos" da conversa: alguma mensagem pede a atenção da equipe de
 * projetos e ainda não foi respondida nem marcada como tratada.
 * → { aberto: true, mensagens, desde } ou null (sem alerta aberto)
 */
export function alertaDaConversa(conversa) {
  const alerta = conversa?.alerta_projetos;
  if (!alerta?.aberto) return null;
  const mensagens = Number(alerta.mensagens) || 0;
  return { aberto: true, mensagens, desde: alerta.desde || null };
}

/** "2 mensagens para a equipe de projetos" (sem número quando a API não o manda). */
export function textoDoAlerta(alerta) {
  if (!alerta) return "";
  if (!alerta.mensagens) return "mensagem para a equipe de projetos";
  return `${plural(alerta.mensagens, "mensagem", "mensagens")} para a equipe de projetos`;
}

/**
 * O ÚNICO sinal de cor de uma linha da lista de conversas. Responde a uma
 * pergunta só: "alguém da equipe precisa olhar aqui?".
 * - Selo "Projetos": mensagem para a equipe de projetos ainda aberta. É o
 *   sinal principal: vence as pendências e o ponto.
 * - Número: pendências abertas (registradas, contáveis).
 * - Ponto: sem pendência registrada, mas a prévia diz que esperam a equipe.
 * - Nada: ninguém esperando a equipe.
 * Laranja (warning) por padrão; vermelho (danger) só com urgência crítica.
 */
export function sinalDaConversa(conversa) {
  if (!conversa) return null;
  const alerta = alertaDaConversa(conversa);
  if (alerta) {
    return {
      tipo: "projetos",
      valor: alerta.mensagens || null,
      tom: "warning",
      rotulo: `Alerta Projetos: ${textoDoAlerta(alerta)}`,
    };
  }
  const previa = conversa.previa || null;
  const tom = (previa?.urgencia ?? 0) >= URGENCIA_CRITICA ? "danger" : "warning";
  const abertas = Number(conversa.pendencias_abertas) || 0;
  if (abertas > 0) {
    return { tipo: "contador", valor: abertas, tom, rotulo: plural(abertas, "pendência aberta", "pendências abertas") };
  }
  if (previa?.quem_espera === "equipe") return { tipo: "ponto", tom, rotulo: "Aguardando a equipe" };
  return null;
}

/**
 * Marca "Projetos" de uma mensagem: "ativa" (faz parte do alerta aberto, com
 * cor), "antiga" (já tratada ou respondida, neutra) ou null (não é para a
 * equipe de projetos). Sem `desde`, toda mensagem marcada conta no alerta aberto.
 */
export function marcaDeProjetos(mensagem, alerta) {
  if (!mensagem?.projetos) return null;
  if (!alerta?.aberto) return "antiga";
  const desde = dataValida(alerta.desde);
  const enviada = dataValida(mensagem.enviada_em);
  if (desde && enviada && enviada < desde) return "antiga";
  return "ativa";
}

/** Iniciais do avatar: "ANA TOZZI" → "AT"; "1418 - Ana" → "A"; vazio → "?". */
export function iniciais(nome) {
  const palavras = String(nome || "")
    .replace(/[^\p{L}\s]/gu, " ")
    .split(/\s+/)
    .filter(Boolean);
  if (!palavras.length) return "?";
  const primeira = palavras[0][0];
  const ultima = palavras.length > 1 ? palavras[palavras.length - 1][0] : "";
  return `${primeira}${ultima}`.toUpperCase();
}

function dataValida(iso) {
  if (!iso) return null;
  const data = new Date(iso);
  return Number.isNaN(data.getTime()) ? null : data;
}

function mesmoDia(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function ontemDe(agora) {
  const ontem = new Date(agora);
  ontem.setDate(agora.getDate() - 1);
  return ontem;
}

/** "17:05" (hora local de um datetime com fuso). */
export function horaCurta(iso) {
  const data = dataValida(iso);
  return data ? data.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "";
}

/** Hora na lista, como no WhatsApp: "17:05", "Ontem", "23/10" ou "23/10/25". */
export function horaNaLista(iso, agora = new Date()) {
  const data = dataValida(iso);
  if (!data) return "";
  if (mesmoDia(data, agora)) return horaCurta(iso);
  if (mesmoDia(data, ontemDe(agora))) return "Ontem";
  const opcoes = data.getFullYear() === agora.getFullYear()
    ? { day: "2-digit", month: "2-digit" }
    : { day: "2-digit", month: "2-digit", year: "2-digit" };
  return data.toLocaleDateString("pt-BR", opcoes);
}

/** Separador de dia das mensagens: "Hoje", "Ontem", "23 de outubro" (ano só se for outro). */
export function rotuloDoDia(iso, agora = new Date()) {
  const data = dataValida(iso);
  if (!data) return "";
  if (mesmoDia(data, agora)) return "Hoje";
  if (mesmoDia(data, ontemDe(agora))) return "Ontem";
  const opcoes = data.getFullYear() === agora.getFullYear()
    ? { day: "numeric", month: "long" }
    : { day: "numeric", month: "long", year: "numeric" };
  return data.toLocaleDateString("pt-BR", opcoes);
}

function chaveDoDia(iso) {
  const data = dataValida(iso);
  return data ? `${data.getFullYear()}-${data.getMonth() + 1}-${data.getDate()}` : "sem-data";
}

/** Marca as mensagens da equipe num bloco só, mesmo que alguém de fora tenha o mesmo nome. */
const AUTOR_DA_EQUIPE = Symbol("equipe");

/**
 * Nome de quem escreveu, como a API manda: a equipe sai com o nome que a
 * unidade configurou na Central; os outros, resolvidos pela agenda do celular
 * (senão o perfil do WhatsApp ou o número).
 */
export function autorDaMensagem(mensagem) {
  if (!mensagem) return "";
  const autor = String(mensagem.autor || "").trim();
  if (mensagem.de_mim) return autor || "Equipe";
  return autor || "Contato";
}

/** Quem é a pessoa para separar os blocos: o id do contato quando vem, senão o nome. */
function chaveDoAutor(mensagem) {
  if (mensagem.de_mim) return AUTOR_DA_EQUIPE;
  return mensagem.autor_id || autorDaMensagem(mensagem);
}

/**
 * Mensagens (em ordem de envio) separadas por dia e marcadas em blocos por
 * autor: o nome aparece e o balão ganha a ponta só no início do bloco, como no
 * WhatsApp. Cada dia é um grupo, para o rótulo do dia "grudar" só no seu trecho.
 * → [{ chave, rotulo, itens: [{ mensagem, inicioDoBloco, fimDoBloco }] }]
 */
export function agruparMensagens(mensagens, agora = new Date()) {
  const dias = [];
  let autorAtual = null;
  (mensagens || []).forEach((mensagem) => {
    const chave = chaveDoDia(mensagem.enviada_em);
    let dia = dias[dias.length - 1];
    if (!dia || dia.chave !== chave) {
      dia = { chave, rotulo: rotuloDoDia(mensagem.enviada_em, agora), itens: [] };
      dias.push(dia);
      autorAtual = null;
    }
    const autor = chaveDoAutor(mensagem);
    const anterior = dia.itens[dia.itens.length - 1];
    const inicioDoBloco = autor !== autorAtual;
    if (inicioDoBloco && anterior) anterior.fimDoBloco = true;
    dia.itens.push({ mensagem, inicioDoBloco, fimDoBloco: false });
    autorAtual = autor;
  });
  dias.forEach((dia) => {
    dia.itens[dia.itens.length - 1].fimDoBloco = true;
  });
  return dias;
}

/** A mensagem mudou (editada, apagada, texto que saiu pela retenção)? */
function mensagemMudou(antes, depois) {
  return antes.texto !== depois.texto || antes.editada !== depois.editada || antes.apagada !== depois.apagada
    || antes.autor !== depois.autor || antes.projetos !== depois.projetos
    || Boolean(antes.imagem) !== Boolean(depois.imagem);
}

/**
 * Junta a página mais recente (lida de novo) às mensagens já na tela: troca as
 * que mudaram, acrescenta as novas e mantém a ordem de envio. Sem mudança,
 * devolve a MESMA lista (a tela não re-renderiza à toa).
 */
export function juntarMensagens(atuais, recentes) {
  const lista = atuais || [];
  if (!recentes?.length) return lista;
  const porId = new Map(lista.map((m) => [m.id, m]));
  let mudou = false;
  recentes.forEach((m) => {
    const antes = porId.get(m.id);
    if (!antes || mensagemMudou(antes, m)) {
      porId.set(m.id, m);
      mudou = true;
    }
  });
  if (!mudou) return lista;
  const quando = (m) => dataValida(m.enviada_em)?.getTime() ?? 0;
  return [...porId.values()].sort((a, b) => quando(a) - quando(b));
}

/** "1:05" a partir de segundos. */
export function duracaoDoAudio(segundos) {
  const total = Math.max(0, Math.round(Number(segundos) || 0));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

const ROTULO_DA_MIDIA = Object.freeze({
  imagem: "Imagem",
  audio: "Áudio",
  video: "Vídeo",
  documento: "Documento",
  figurinha: "Figurinha",
  localizacao: "Localização",
  contato: "Contato",
  enquete: "Enquete",
});

/** Mídia de uma mensagem: rótulo e detalhe ("Áudio", "0:42"). `null` para texto. */
export function midiaDaMensagem(mensagem) {
  if (!mensagem || !ROTULO_DA_MIDIA[mensagem.tipo]) return null;
  const rotulo = ROTULO_DA_MIDIA[mensagem.tipo];
  if (mensagem.tipo === "documento") return { tipo: "documento", rotulo: mensagem.midia_nome || rotulo, detalhe: "Documento" };
  if (mensagem.tipo === "audio") {
    return { tipo: "audio", rotulo, detalhe: mensagem.duracao_s ? duracaoDoAudio(mensagem.duracao_s) : "" };
  }
  return { tipo: mensagem.tipo, rotulo, detalhe: mensagem.midia_nome || "" };
}

/**
 * Segunda linha do item da lista: quem mandou (a equipe, ou o autor em grupo),
 * o ícone da mídia e o texto numa linha só.
 */
export function resumoDaUltimaMensagem(ultima, { grupo = false } = {}) {
  if (!ultima) return { autor: "", icone: null, texto: "Nenhuma mensagem guardada" };
  const autor = ultima.de_mim || grupo ? autorDaMensagem(ultima) : "";
  const texto = String(ultima.texto || "").replace(/\s+/g, " ").trim();
  const midia = midiaDaMensagem(ultima);
  if (midia) return { autor, icone: midia.tipo, texto: texto || midia.rotulo };
  return { autor, icone: null, texto: texto || "Mensagem" };
}

/** Trecho de uma mensagem para a citação (responder): o texto ou o rótulo da mídia. */
export function trechoDaMensagem(mensagem) {
  if (!mensagem) return "";
  const texto = String(mensagem.texto || "").replace(/\s+/g, " ").trim();
  const midia = midiaDaMensagem(mensagem);
  // A apagada guarda o texto desde 04/11/2026; a de antes ficou sem ele.
  if (mensagem.apagada) {
    if (!texto && !midia) return "Mensagem apagada";
    return `${midia ? (texto ? `${midia.rotulo}: ${texto}` : midia.rotulo) : texto} (apagada)`;
  }
  if (midia) return texto ? `${midia.rotulo}: ${texto}` : midia.rotulo;
  return texto || "Mensagem";
}

/** Dica do selo "Apagada": quando apagaram e que o texto ficou guardado. */
export function dicaDaApagada(mensagem, agora = new Date()) {
  const quando = mensagem?.apagada_em ? horaNaLista(mensagem.apagada_em, agora) : "";
  const hora = mensagem?.apagada_em ? horaCurta(mensagem.apagada_em) : "";
  const momento = quando && quando !== hora ? `${quando} ${hora}` : hora;
  return momento
    ? `Apagada no WhatsApp (${momento}). O texto ficou guardado aqui.`
    : "Apagada no WhatsApp. O texto ficou guardado aqui.";
}

/** Limite do WhatsApp para o que a Central manda (arquivo ou áudio). */
export const TAMANHO_MAXIMO_DO_ENVIO = 16 * 1024 * 1024;
export const TAMANHO_MAXIMO_DO_TEXTO = 4096;

/** "340 KB", "1,2 MB". */
export function tamanhoLegivel(bytes) {
  const valor = Math.max(0, Number(bytes) || 0);
  if (valor < 1024 * 1024) return `${Math.max(1, Math.round(valor / 1024))} KB`;
  return `${(valor / (1024 * 1024)).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`;
}

/** Como mostrar o anexo antes de enviar: foto (miniatura), vídeo ou documento. */
export function tipoDoAnexo(arquivo) {
  const mime = String(arquivo?.type || "");
  if (mime.startsWith("image/")) return "imagem";
  if (mime.startsWith("video/")) return "video";
  return "documento";
}

/** Formatos de gravação, na ordem de preferência (o monitor converte para OGG/Opus). */
const FORMATOS_DE_AUDIO = Object.freeze([
  { mime: "audio/webm;codecs=opus", extensao: "webm" },
  { mime: "audio/ogg;codecs=opus", extensao: "ogg" },
  { mime: "audio/mp4", extensao: "m4a" },
  { mime: "audio/webm", extensao: "webm" },
]);

/** Primeiro formato que o navegador grava, ou null (sem MediaRecorder). */
export function formatoDeAudio(Gravador) {
  if (!Gravador || typeof Gravador.isTypeSupported !== "function") return null;
  return FORMATOS_DE_AUDIO.find((f) => Gravador.isTypeSupported(f.mime)) || null;
}

/** Estado do comando na tela: ainda indo ("enviando") ou parado com erro ("falhou"). */
export function situacaoDoComando(comando) {
  if (!comando) return null;
  if (comando.estado === "falhou") return "falhou";
  if (comando.estado === "pendente" || comando.estado === "enviando") return "enviando";
  return null;
}

/** "enviar_audio" ou "audio" → "audio" (o contrato fala dos dois jeitos). */
export function tipoDoComando(comando) {
  return String(comando?.tipo || "").replace(/^enviar_/, "");
}

/** Folga entre o relógio do servidor e o do celular ao casar o comando com a mensagem. */
const FOLGA_DO_RELOGIO_MS = 2 * 60 * 1000;

/**
 * A mensagem que um comando mandou já voltou pelo monitor? Casa pelo id
 * externo quando a API o traz; senão, por uma mensagem da equipe depois do
 * comando com o mesmo texto (ou o mesmo arquivo, ou um áudio).
 */
export function comandoJaChegou(comando, mensagens) {
  if (!comando || !mensagens?.length) return false;
  if (comando.mensagem_id && mensagens.some((m) => m.id_externo === comando.mensagem_id)) return true;
  const criado = dataValida(comando.criado_em);
  if (!criado) return false;
  const desde = criado.getTime() - FOLGA_DO_RELOGIO_MS;
  const tipo = tipoDoComando(comando);
  const texto = String(comando.texto || "").trim();
  return mensagens.some((m) => {
    if (!m.de_mim) return false;
    const enviada = dataValida(m.enviada_em);
    if (!enviada || enviada.getTime() < desde) return false;
    if (tipo === "audio") return m.tipo === "audio";
    if (tipo === "arquivo") {
      const mesmoArquivo = Boolean(comando.arquivo_nome) && m.midia_nome === comando.arquivo_nome;
      return mesmoArquivo || (Boolean(texto) && String(m.texto || "").trim() === texto);
    }
    return Boolean(texto) && String(m.texto || "").trim() === texto;
  });
}

/**
 * De onde veio a prévia e quando: "Prévia das 13:00" (rodada em lote, sem
 * gastar nada ao abrir) ou "Atualizada às 10:42" (pedida na hora).
 * Ontem e outros dias entram na frase ("Prévia de ontem, 13:00").
 */
export function textoDaPrevia(previa, agora = new Date()) {
  const data = dataValida(previa?.gerada_em);
  if (!data) return "";
  const hora = horaCurta(previa.gerada_em);
  const dia = data.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  const hoje = mesmoDia(data, agora);
  const ontem = mesmoDia(data, ontemDe(agora));
  if (previa.origem === "rodada") {
    if (hoje) return `Prévia das ${hora}`;
    return ontem ? `Prévia de ontem, ${hora}` : `Prévia de ${dia}, ${hora}`;
  }
  if (hoje) return `Atualizada às ${hora}`;
  return ontem ? `Atualizada ontem às ${hora}` : `Atualizada em ${dia} às ${hora}`;
}

/** "desde 09:31", "desde ontem" ou "desde 27/10". */
export function textoDesde(iso, agora = new Date()) {
  const data = dataValida(iso);
  if (!data) return "";
  if (mesmoDia(data, agora)) return `desde ${horaCurta(iso)}`;
  if (mesmoDia(data, ontemDe(agora))) return "desde ontem";
  return `desde ${data.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })}`;
}

/** "o texto é apagado em 5 dias" a partir de `quarentena_ate` (data, AAAA-MM-DD). */
export function textoDaQuarentena(quarentenaAte, agora = new Date()) {
  const limite = dataLocalDe(quarentenaAte);
  if (!limite) return "";
  const hoje = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate());
  const dias = Math.round((limite - hoje) / 86400000);
  if (dias <= 0) return "o texto é apagado hoje se não for classificada";
  if (dias === 1) return "o texto é apagado amanhã se não for classificada";
  return `o texto é apagado em ${dias} dias se não for classificada`;
}

/** "14:20" hoje; "Ontem 14:20" ou "23/10 14:20" antes. */
function diaEHora(iso, agora) {
  const hora = horaCurta(iso);
  const dia = horaNaLista(iso, agora);
  return dia === hora ? hora : [dia, hora].filter(Boolean).join(" ");
}

/**
 * O que está pendente para Projetos, para a prévia:
 * → { resumo: "7 mensagens sem resposta desde ontem", itens: [{ autor, quando, texto }], restantes }
 * `itens` (as mais novas primeiro) é null para quem não pode ler as mensagens;
 * `restantes`, as mais antigas que a lista não traz. Sem alerta aberto, null.
 */
export function pendenteParaProjetos(conversa, agora = new Date()) {
  const alerta = alertaDaConversa(conversa);
  if (!alerta) return null;
  const desde = textoDesde(alerta.desde, agora);
  const quantas = alerta.mensagens ? plural(alerta.mensagens, "mensagem", "mensagens") : "Mensagem";
  const resumo = `${quantas} sem resposta${desde ? ` ${desde}` : ""}`;
  const trechos = conversa.alerta_projetos?.trechos;
  if (!Array.isArray(trechos)) return { resumo, itens: null, restantes: 0 };
  const itens = trechos.map((t) => ({
    autor: autorDaMensagem(t),
    quando: diaEHora(t.enviada_em, agora),
    texto: trechoDaMensagem(t),
  }));
  return { resumo, itens, restantes: Math.max(0, alerta.mensagens - itens.length) };
}

/** Linha de baixo do cabeçalho da conversa: tipo · pedido · cliente (ou o aviso de quarentena). */
export function subtituloDaConversa(conversa, agora = new Date()) {
  if (!conversa) return "";
  const tipo = ROTULO_CLASSIFICACAO[conversa.classificacao] || "A classificar";
  if (conversa.classificacao === "desconhecido") {
    const quarentena = textoDaQuarentena(conversa.quarentena_ate, agora);
    return quarentena ? `${tipo} · ${quarentena}` : tipo;
  }
  const pedido = conversa.numero_pedido ? `Pedido ${conversa.numero_pedido}` : "";
  return [tipo, pedido, pedido ? conversa.cliente_nome : ""].filter(Boolean).join(" · ");
}

export const TIPOS_PENDENCIA = Object.freeze([
  { valor: "aguardando_equipe", rotulo: "Aguardando a equipe" },
  { valor: "sem_resposta", rotulo: "Sem resposta" },
  { valor: "aguardando_cliente", rotulo: "Aguardando o cliente" },
  { valor: "prazo", rotulo: "Prazo" },
]);

export const ETAPAS_DO_PROJETO = Object.freeze([
  { valor: "elaboracao", rotulo: "Elaboração" },
  { valor: "revisao", rotulo: "Revisão" },
  { valor: "aprovacao", rotulo: "Aprovação" },
]);

/**
 * Leitura da IA por ambiente (`ia.estado`). Mesma régua de cor da prévia:
 * laranja quando a equipe deve, azul quando o cliente deve, verde concluído,
 * resto neutro.
 */
export const ESTADO_DO_AMBIENTE = Object.freeze({
  esperando_equipe: { rotulo: "Esperando a equipe", tom: "warning" },
  esperando_cliente: { rotulo: "Esperando o cliente", tom: "info" },
  andando: { rotulo: "Andando", tom: "neutral" },
  concluido: { rotulo: "Concluído", tom: "success" },
  sem_conversa: { rotulo: "Sem conversa", tom: "neutral" },
});

/** Ordem dos filtros e das contagens por estado na ficha. */
export const ORDEM_DOS_ESTADOS = Object.freeze([
  "esperando_equipe",
  "esperando_cliente",
  "andando",
  "concluido",
  "sem_conversa",
]);

/** Estado do ambiente segundo a IA; sem leitura ainda, `null`. */
export function estadoDoAmbiente(ambiente) {
  const estado = ambiente?.ia?.estado;
  return ESTADO_DO_AMBIENTE[estado] ? estado : null;
}

/** Contagem por estado da IA nos ambientes, na ordem dos filtros. */
export function contarEstados(ambientes) {
  const totais = {};
  (ambientes || []).forEach((a) => {
    const estado = estadoDoAmbiente(a);
    if (estado) totais[estado] = (totais[estado] || 0) + 1;
  });
  return ORDEM_DOS_ESTADOS
    .filter((estado) => totais[estado])
    .map((estado) => ({ estado, ...ESTADO_DO_AMBIENTE[estado], total: totais[estado] }));
}

/** Nome comparável de ambiente: sem acento, sem caixa e sem espaço sobrando. */
export function chaveDoAmbiente(nome) {
  return String(nome || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/**
 * Pendências e decisões de UM ambiente: as que a API já pôs no ambiente ou,
 * se não vierem nele, as do pedido com o mesmo nome de ambiente.
 */
export function ligadosAoAmbiente(ambiente, { pendencias = [], decisoes = [] } = {}) {
  const chave = chaveDoAmbiente(ambiente?.ambiente);
  const doAmbiente = (lista) => lista.filter((item) => chave && chaveDoAmbiente(item.ambiente) === chave);
  return {
    pendencias: Array.isArray(ambiente?.pendencias) ? ambiente.pendencias : doAmbiente(pendencias),
    decisoes: Array.isArray(ambiente?.decisoes) ? ambiente.decisoes : doAmbiente(decisoes),
  };
}

/** Tom da linha do cliente: atraso > travado/divergência > neutro. */
export function tomDoCliente(cliente) {
  if (!cliente) return "neutral";
  if (cliente.atraso_dias > 0) return "danger";
  if (cliente.pendencias_travando > 0 || (cliente.divergencias || []).length > 0) return "warning";
  return "neutral";
}

/** "8 dias de atraso" / "no prazo". */
export function textoAtraso(dias) {
  if (!dias || dias <= 0) return "no prazo";
  return `${plural(dias, "dia", "dias")} de atraso`;
}

/** "hoje 14:05", "ontem", "28/10" — para a última mensagem. */
export function quandoFoi(iso, agora = new Date()) {
  if (!iso) return "—";
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return "—";
  const mesmoDia = (a, b) => a.toDateString() === b.toDateString();
  const ontem = new Date(agora);
  ontem.setDate(agora.getDate() - 1);
  if (mesmoDia(data, agora)) {
    return `hoje ${data.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
  }
  if (mesmoDia(data, ontem)) return "ontem";
  return data.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

/** Faixa de status: monitor online/offline/QR e as últimas leituras. */
export function descreverStatus(status, agora = new Date()) {
  if (!status) return { tom: "neutral", monitor: "Monitor sem notícias", detalhes: [] };
  const ultimoSinal = status.monitor_ultimo_sinal ? new Date(status.monitor_ultimo_sinal) : null;
  let monitor;
  let tom;
  if (status.monitor_pede_qr) {
    monitor = "Monitor esperando o QR";
    tom = "warning";
  } else if (status.monitor_online) {
    monitor = "Monitor online";
    tom = "success";
  } else if (ultimoSinal) {
    // "hoje 14:05" vira só "14:05"; outro dia fica com a data.
    const quando = quandoFoi(status.monitor_ultimo_sinal, agora).replace(/^hoje /, "");
    monitor = `Monitor offline desde ${quando}`;
    tom = "danger";
  } else {
    monitor = "Monitor ainda não conectado";
    tom = "neutral";
  }
  const detalhes = [
    status.planilha_lida_em ? `Planilha lida ${quandoFoi(status.planilha_lida_em, agora)}` : "Planilha ainda não lida",
    status.ultima_analise_em ? `Última análise ${quandoFoi(status.ultima_analise_em, agora)}` : "Sem análise ainda",
  ];
  return { tom, monitor, detalhes };
}

/** "Prazo 03/11" ou "sem prazo". */
export function textoPrazo(iso) {
  return iso ? `Prazo ${formatarDataLocal(iso).slice(0, 5)}` : "sem prazo";
}

/** Contagem de itens por etapa do projetista: "3 elaboração · 1 revisão". */
export function resumoDoProjetista(coluna) {
  return ETAPAS_DO_PROJETO
    .map(({ valor, rotulo }) => ({ rotulo: rotulo.toLowerCase(), total: (coluna?.[valor] || []).length }))
    .filter((item) => item.total > 0)
    .map((item) => `${item.total} ${item.rotulo}`)
    .join(" · ");
}

/** Agrupa a lista de pendências por cliente (checklist por cliente). */
export function agruparPorCliente(pendencias) {
  const grupos = new Map();
  (pendencias || []).forEach((p) => {
    const chave = p.numero_pedido;
    if (!grupos.has(chave)) grupos.set(chave, { numero_pedido: chave, cliente: p.cliente || "", itens: [] });
    grupos.get(chave).itens.push(p);
  });
  return [...grupos.values()];
}
